import React, { useContext, useEffect, useRef, useState } from 'react'; // Added useState
import { Link, useLocation } from 'react-router-dom';
import styles from './styles/ClassroomSidebar.module.css';
import { ClassroomContext } from '../../context/ClassroomContext';
import NotificationContext from '../../context/NotificationContext';
import { classroomImages } from '../../utils/utils';
import { regenerateClassroomCode } from '../../services/ClassroomServices';
import {
    FaTachometerAlt, FaUsers, FaCog, FaBullhorn, FaScroll,
    FaFolderOpen, FaInfoCircle, FaChevronLeft, FaTasks, FaComment,
    FaCopy, FaSyncAlt, FaPlus, // New Icons
    FaCalendarAlt
} from 'react-icons/fa';
import { calendarPath } from './classroomPaths';

const ClassroomSidebar = () => {
    const { classroom, setClassroom, clearCurrentClassroom, isOwner, isTeacher } = useContext(ClassroomContext);
    const { getUnseenAnnouncementCount } = useContext(NotificationContext);
    const [copyStatus, setCopyStatus] = useState('Copy');
    const location = useLocation();

    const randomImageRef = useRef(null);
    if (!randomImageRef.current) {
        const randomIndex = Math.floor(Math.random() * classroomImages.length);
        randomImageRef.current = classroomImages[randomIndex];
    }
    const fallbackImage = randomImageRef.current;

    const handleRegenerateCode = async () => {
        try {
            const data = await regenerateClassroomCode(classroom._id);
            setClassroom(prev => ({ ...prev, classroomCode: data.classroomCode }));
        } catch (err) {
            console.error("Error updating code:", err);
            alert("Failed to update classroom code.");
        }
    };

    // --- New Logic: Copy to Clipboard ---
    const handleCopy = () => {
        if (classroom?.classroomCode) {
            navigator.clipboard.writeText(classroom.classroomCode);
            setCopyStatus('Copied!');
            setTimeout(() => setCopyStatus('Copy'), 2000);
        }
    };

    const getActiveRoutes = (pathname) => {
        const segments = pathname.split('/');
        let mainRoute = segments[3];
        let subRoute = segments[4]; 

        if (['a', 'l', 'f', 'assignments', 'gt', 'gs', 'feedback'].includes(mainRoute)) {
            subRoute = mainRoute;
            mainRoute = 'd';
        }
        return { main: mainRoute, sub: subRoute };
    };

    const { main, sub } = getActiveRoutes(location.pathname);

    // Cleared by visiting the announcements page — see ClassroomAnnouncements.
    const unreadAnnouncements = getUnseenAnnouncementCount(classroom?._id);

    const mainLinks = [
        {
            label: 'Dashboard', path: 'd', icon: <FaTachometerAlt className={styles.icon} />,
            subLinks: [
                { label: 'Announcements', path: 'a', icon: <FaBullhorn style={{ marginRight: '6px' }} />, unreadCount: unreadAnnouncements },
                { label: 'Grades', path: isTeacher ? 'gt' : 'gs', icon: <FaScroll style={{ marginRight: '6px' }} /> },
                { label: 'Assignments', path: 'assignments', icon: <FaTasks style={{ marginRight: '6px' }} /> },
                { label: 'Feedback', path: 'feedback', icon: <FaComment style={{ marginRight: '6px' }} /> },
            ],
            alwaysOpen: true
        },
        { label: 'About', path: 'i', icon: <FaInfoCircle className={styles.icon} /> },
        { label: 'People', path: 'p', icon: <FaUsers className={styles.icon} /> },
        { label: 'Settings', path: 'settings', icon: <FaCog className={styles.icon} /> }
    ];


    const handleBackClick = () => {
        clearCurrentClassroom();
    };

    return (
        <div className={styles.sidebarContainer}>
            <Link to="/my-classrooms" className={styles.backIcon} onClick={handleBackClick}>
                <FaChevronLeft />
            </Link>

            <div className={styles.classroomInfoHeader}>
                {classroom ? (
                    <>
                        <div className={styles.classroomName}>{classroom.name}</div>
                        {classroom.owner && (
                            <div className={styles.classroomOwner}>
                                Owner: {classroom.owner.username}
                            </div>
                        )}
                        <img
                            src={(classroom.image && classroom.image.trim()) ? classroom.image : fallbackImage}
                            alt={classroom.name || 'Classroom'}
                            className={styles.classroomImage}
                        />
                    </>
                ) : (
                    <div className={styles.classroomInfoPlaceholder}></div>
                )}
            </div>

            <ul className={styles.navList}>
                {mainLinks.map(mainItem => {
                    const isMainActive = main === mainItem.path;
                    return (
                        <li key={mainItem.path}>
                            <Link
                                to={`/my-classrooms/${classroom?._id}/${mainItem.path}`}
                                className={`${styles.navItem} ${isMainActive && (!mainItem.subLinks || !mainItem.subLinks.some(s => s.path === sub)) ? styles.active : ''}`}
                            >
                                {mainItem.icon}
                                {mainItem.label}
                            </Link>

                            {mainItem.alwaysOpen && mainItem.subLinks && (
                                <ul className={styles.subNavList}>
                                    {mainItem.subLinks.map(subItem => {
                                        const isSubActive = main === mainItem.path && sub === subItem.path;
                                        return (
                                            <li key={subItem.path}>
                                                <Link
                                                    to={`/my-classrooms/${classroom?._id}/${subItem.path}`}
                                                    className={`${styles.subNavItem} ${isSubActive ? styles.active : ''}`}
                                                >
                                                    {subItem.icon}
                                                    {subItem.label}
                                                    {subItem.unreadCount > 0 && (
                                                        <span
                                                            className={styles.unreadDot}
                                                            title={`${subItem.unreadCount} unread`}
                                                        />
                                                    )}
                                                </Link>
                                            </li>
                                        );
                                    })}
                                </ul>
                            )}
                        </li>
                    );
                })}
                {/* The global calendar, opened filtered to this classroom. Leaving the
                    classroom, so it clears it like the back link does. */}
                {classroom && (
                    <li>
                        <Link
                            to={calendarPath([classroom._id])}
                            className={styles.navItem}
                            onClick={clearCurrentClassroom}
                        >
                            <FaCalendarAlt className={styles.icon} />
                            Calendar
                        </Link>
                    </li>
                )}
            </ul>

            {/* NEW: Classroom Code Section */}
            {classroom && (isOwner || isTeacher) && (
                <div className={styles.codeSection}>
                    <div className={styles.codeHeader}>
                        <span>Classroom Code</span>
                        {classroom.classroomCode && (
                            <button className={styles.copyBtn} onClick={handleCopy} title="Copy Code">
                                {copyStatus === 'Copied!' ? <span className={styles.copiedText}>Copied!</span> : <FaCopy />}
                            </button>
                        )}
                    </div>
                    
                    {classroom.classroomCode ? (
                        <div className={styles.codeDisplay}>
                            {classroom.classroomCode}
                        </div>
                    ) : (
                        <div className={styles.noCodeText}>No code generated</div>
                    )}

                    <button className={styles.regenerateBtn} onClick={handleRegenerateCode}>
                        {classroom.classroomCode ? <><FaSyncAlt /> Regenerate</> : <><FaPlus /> Generate Code</>}
                    </button>
                </div>
            )}
        </div>
    );
};

export default ClassroomSidebar;